'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { Fragment, useState } from 'react'
import { ListboxOption, ListboxLabel, ListboxDescription } from 'V2.0/common/listbox'

export { ListboxOption, ListboxLabel, ListboxDescription }

// Types
export type MultiListboxItem<T> = {
  id: T,
  value: T
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

export type MultiListboxGroup<T> = {
  label: string
  labelNode?: React.ReactNode
  sticky?: boolean
  options: MultiListboxItem<T>[]
}

function isGroupedOptions<T>(
  options: MultiListboxItem<T>[] | MultiListboxGroup<T>[]
): options is MultiListboxGroup<T>[] {
  return Array.isArray(options) && options.length > 0 && 'options' in options[0]
}

function getAllItems<T>(options: MultiListboxItem<T>[] | MultiListboxGroup<T>[]): MultiListboxItem<T>[] {
  if (isGroupedOptions(options)) {
    return options.flatMap((group) => group.options)
  }
  return options
}

function Section({
  label,
  sticky,
  collapsed,
  onToggle,
  children,
}: {
  label: React.ReactNode
  sticky?: boolean
  collapsed: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="relative">
      <div
        className={clsx(
          'flex items-center px-3 py-1 font-semibold text-zinc-700 dark:text-zinc-200 z-10 cursor-pointer select-none min-h-10 shadow-sm bg-gray-50',
          sticky && 'sticky top-0 border-b'
        )}
        onClick={onToggle}
        role="button"
        aria-expanded={!collapsed}
        tabIndex={0}
      >
        <span className="flex-1 text-sm">{label}</span>
        <svg
          className={clsx('transition-transform size-4 ml-2', collapsed ? 'rotate-0' : 'rotate-90')}
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {!collapsed && <div>{children}</div>}
    </div>
  )
}

export function MultiListbox<T>({
  className,
  placeholder,
  autoFocus,
  'aria-label': ariaLabel,
  options,
  displayValue,
  singleValueAsText,
  matchButtonWidth,
  ...props
}: {
  className?: string
  placeholder?: React.ReactNode
  autoFocus?: boolean
  'aria-label'?: string
  options: MultiListboxItem<T>[] | MultiListboxGroup<T>[]
  displayValue?: (value: T) => string
  // When true, a lone selected value renders as plain text instead of a chip.
  singleValueAsText?: boolean
  // When true, the panel is pinned to the trigger's width instead of sizing to
  // its widest option, which can otherwise overflow a narrow container.
  matchButtonWidth?: boolean
} & Omit<Headless.ListboxProps<typeof Fragment, T[]>, 'as' | 'multiple'>) {
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})

  const toggleSection = (label: string) => {
    setCollapsedSections((prev) => ({ ...prev, [label]: !prev[label] }))
  }

  const allItems = getAllItems(options)

  const getDisplayText = (item: T): string => {
    if (displayValue) return displayValue(item)
    const found = allItems.find((opt) => opt.value === item)
    return found?.label ?? String(item)
  }

  const renderOptions = () => {
    if (isGroupedOptions(options)) {
      return options.map((group) => (
        <Section
          key={group.label}
          label={group.labelNode ?? group.label}
          sticky={group.sticky}
          collapsed={!!collapsedSections[group.label]}
          onToggle={() => toggleSection(group.label)}
        >
          {group.options.map((opt) => (
            <ListboxOption key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
              {opt.icon}
              <ListboxLabel>{opt.label}</ListboxLabel>
            </ListboxOption>
          ))}
        </Section>
      ))
    }
    return options.map((opt) => (
      <ListboxOption key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
        {opt.icon}
        <ListboxLabel>{opt.label}</ListboxLabel>
      </ListboxOption>
    ))
  }

  return (
    <Headless.Listbox {...props} multiple={true}>
      {({ value }) => (
        <>
          <Headless.ListboxButton
            autoFocus={autoFocus}
            data-slot="control"
            aria-label={ariaLabel}
            className={clsx([
              className,
              // Basic layout
              'group relative block w-full',
              // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
              'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
              // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
              'dark:before:hidden',
              // Hide default focus styles
              'focus:outline-hidden',
              // Focus ring
              'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset data-focus:after:ring-2 data-focus:after:ring-blue-500',
              // Disabled state
              "data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none",
            ])}
          >
            <span
              className={clsx([
                // Basic layout
                'relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
                // Set minimum height for when no value is selected
                'min-h-11 sm:min-h-9',
                // Horizontal padding
                'pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]',
                // Typography
                'text-left text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
                // Border
                'border border-zinc-950/10 group-data-active:border-zinc-950/20 group-data-hover:border-zinc-950/20 dark:border-white/10 dark:group-data-active:border-white/20 dark:group-data-hover:border-white/20',
                // Background color
                'bg-transparent dark:bg-white/5',
                // Invalid state
                 "group-data-[invalid]:border-red-500 group-data-[invalid]:group-data-[hover]:border-red-500 group-data-[invalid]:dark:border-red-600 group-data-[invalid]:data-[hover]:dark:border-red-600",
                // Disabled state
                "group-data-[disabled]:border-zinc-950/20 group-data-[disabled]:opacity-100 group-data-[disabled]:dark:border-white/15 group-data-[disabled]:dark:bg-white/[2.5%] dark:data-[hover]:group-data-[disabled]:border-white/15",
              ])}
            >
              {value.length === 0 ? (
                <span className="block truncate text-zinc-500 px-2 py-1">{placeholder}</span>
              ) : singleValueAsText && value.length === 1 ? (
                <span className="block truncate px-2 py-1 text-zinc-950 dark:text-white">
                  {getDisplayText(value[0])}
                </span>
              ) : (
                <span className="flex flex-wrap gap-1 p-2">
                  {value.map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 rounded-md bg-zinc-950/5 px-1.5 py-0.5 text-sm/5 dark:bg-white/10"
                    >
                      {getDisplayText(item)}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          props.onChange?.(value.filter((_, i) => i !== index))
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                        }}
                        className="ml-0.5 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                        aria-label={`Remove ${getDisplayText(item)}`}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </span>
              )}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <svg
                className="size-5 stroke-zinc-500 group-data-disabled:stroke-zinc-600 sm:size-4 dark:stroke-zinc-400 forced-colors:stroke-[CanvasText]"
                viewBox="0 0 16 16"
                aria-hidden="true"
                fill="none"
              >
                <path d="M5.75 10.75L8 13L10.25 10.75" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.25 5.25L8 3L5.75 5.25" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </Headless.ListboxButton>
          <Headless.ListboxOptions
            transition
            anchor="bottom start"
            className={clsx(
              // Anchor positioning. `--anchor-max-height` caps the panel: Headless
              // UI writes an inline `max-height: min(var(--anchor-max-height,100vh), …)`,
              // which overrides the `max-h-*` class, so the cap must live here.
              '[--anchor-gap:--spacing(1)] [--anchor-padding:--spacing(4)] [--anchor-max-height:15rem]',
              // Base styles
              'isolate max-h-60 scroll-py-1 rounded-xl p-1 select-none',
              matchButtonWidth
                ? 'w-[var(--button-width)]'
                : 'w-max min-w-[calc(var(--button-width)+1.75rem)]',
              // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
              'outline outline-transparent focus:outline-hidden',
              // Handle scrolling when menu won't fit in viewport
              'overflow-y-scroll overscroll-contain',
              // Popover background
              'bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75',
              // Shadows
              'shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 dark:ring-inset',
              // Transitions
              'transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none'
            )}
          >
            {renderOptions()}
          </Headless.ListboxOptions>
        </>
      )}
    </Headless.Listbox>
  )
}
